import type { AppKey } from '@/features/landing-page/data/app-detail'

/** Rotation + display order on people-security, per the mock (not the
    landing's PEOPLE_APPS order). */
// ponytail: people-security only — the property page gets its own list when built.
export const SHOWCASE_APPS = [
  'alert',
  'evac',
  'livecount',
  'safehuman',
] as const satisfies readonly AppKey[]

/** Property-security showcase, grouped by obligation domain. CompliAssist ships
    as a standalone app (`app`); `module` items are CompliAssist capabilities —
    available today, linked "Avec CompliAssist"; `soon` items are true
    placeholders rendered as "coming soon" cards. Module/soon slugs are plain
    strings, not AppKeys, since they have no own assets or feature routes; their
    name/desc live under the `soon` translation block. Group keys resolve to a
    header label and a generic ghost icon in the component. */
export type PropertyGroupItem =
  | { app: AppKey }
  | { module: string }
  | { soon: string }

export const PROPERTY_GROUPS: readonly {
  key: string
  items: readonly PropertyGroupItem[]
}[] = [
  { key: 'documents', items: [{ app: 'compliassist' }, { module: 'duerp' }] },
  {
    key: 'works',
    items: [
      { soon: 'previassist' },
      { module: 'permisfeu' },
      { module: 'gmassist' },
    ],
  },
  {
    key: 'controls',
    items: [
      { module: 'rondesecu' },
      { module: 'auditsecu' },
      { module: 'formassist' },
    ],
  },
]

/** Display names, used as alt text for the wordmark images in the cards and as
    the feature-badge label. Keyed by every AppKey so feature badges resolve for
    property apps too. */
export const APP_NAMES: Record<AppKey, string> = {
  alert: 'Alert',
  evac: 'Evac',
  livecount: 'LiveCount',
  safehuman: 'SafeHuman',
  compliassist: 'CompliAssist',
}

/** Visible-art aspect ratio (w/h) of each minimal wordmark PNG. The PNGs are
    240×38 with the art left-aligned and transparent padding on the right, so
    a centered `w-auto` box shifts the visible art left. Consumers pair this
    with `object-cover object-left` to crop the padding away. Measured from
    the alpha channel of each PNG. */
export const LOGO_ASPECT: Record<AppKey, number> = {
  alert: 2.8,
  evac: 2.95,
  livecount: 4.87,
  safehuman: 5.4,
  compliassist: 7.0,
}
